import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { useAuth } from './AuthContext';

const ProfileContext = createContext(null);

// ── Avatar library — 20 gradient combos + icon index ─────────────────────────
export const AVATARS = [
  { id: 0,  bg: 'linear-gradient(135deg,#FF0A7A,#7C3AED)', label: 'Pink Burst' },
  { id: 1,  bg: 'linear-gradient(135deg,#3B82F6,#06B6D4)', label: 'Ocean Blue' },
  { id: 2,  bg: 'linear-gradient(135deg,#F59E0B,#EF4444)', label: 'Sunset' },
  { id: 3,  bg: 'linear-gradient(135deg,#10B981,#3B82F6)', label: 'Emerald' },
  { id: 4,  bg: 'linear-gradient(135deg,#8B5CF6,#EC4899)', label: 'Violet' },
  { id: 5,  bg: 'linear-gradient(135deg,#F97316,#EAB308)', label: 'Flame' },
  { id: 6,  bg: 'linear-gradient(135deg,#06B6D4,#10B981)', label: 'Teal' },
  { id: 7,  bg: 'linear-gradient(135deg,#EF4444,#F97316)', label: 'Fire' },
  { id: 8,  bg: 'linear-gradient(135deg,#6366F1,#8B5CF6)', label: 'Indigo' },
  { id: 9,  bg: 'linear-gradient(135deg,#14B8A6,#06B6D4)', label: 'Aqua' },
  { id: 10, bg: 'linear-gradient(135deg,#EC4899,#F97316)', label: 'Coral' },
  { id: 11, bg: 'linear-gradient(135deg,#84CC16,#10B981)', label: 'Lime' },
  { id: 12, bg: 'linear-gradient(135deg,#0EA5E9,#6366F1)', label: 'Sky' },
  { id: 13, bg: 'linear-gradient(135deg,#F43F5E,#EC4899)', label: 'Rose' },
  { id: 14, bg: 'linear-gradient(135deg,#A78BFA,#818CF8)', label: 'Lavender' },
  { id: 15, bg: 'linear-gradient(135deg,#FCD34D,#F97316)', label: 'Gold' },
];

// Kids avatar — special purple with star
export const KIDS_AVATAR = { id: 99, bg: 'linear-gradient(135deg,#7C3AED,#EC4899)', label: 'Kids' };

function storageKey(uid) { return `sf_profiles_${uid}`; }
function activeKey(uid)  { return `sf_active_profile_${uid}`; }

function loadProfiles(uid) {
  try {
    const raw = localStorage.getItem(storageKey(uid));
    return raw ? JSON.parse(raw) : null;
  } catch { return null; }
}
function saveProfiles(uid, profiles) {
  localStorage.setItem(storageKey(uid), JSON.stringify(profiles));
}
function loadActiveId(uid) {
  return localStorage.getItem(activeKey(uid)) || null;
}
function saveActiveId(uid, id) {
  localStorage.setItem(activeKey(uid), id);
}

function makeDefault(user) {
  const name = user?.displayName || user?.email?.split('@')[0] || 'Me';
  return [
    { id: 'p1', name, avatarId: 0, isKids: false, isDefault: true },
  ];
}

export function ProfileProvider({ children }) {
  const { user, isAuthenticated } = useAuth();
  const [profiles,       setProfiles]       = useState([]);
  const [activeProfile,  setActiveProfile]  = useState(null);
  const [showSelector,   setShowSelector]   = useState(false); // "Who's watching?" screen

  // Load profiles from localStorage when user changes
  useEffect(() => {
    if (!isAuthenticated || !user) {
      setProfiles([]);
      setActiveProfile(null);
      return;
    }

    const stored = loadProfiles(user.uid);
    const list   = stored || makeDefault(user);
    if (!stored) saveProfiles(user.uid, list);
    setProfiles(list);

    const savedActiveId = loadActiveId(user.uid);
    const active = list.find(p => p.id === savedActiveId) || list[0];
    setActiveProfile(active);

    // Profile selector overlay is disabled — single profile per account for now.
  }, [user?.uid, isAuthenticated]); // eslint-disable-line

  const selectProfile = useCallback((profile) => {
    if (!user) return;
    setActiveProfile(profile);
    saveActiveId(user.uid, profile.id);
    sessionStorage.setItem(`sf_profile_chosen_${user.uid}`, '1');
    setShowSelector(false);
  }, [user]);

  const addProfile = useCallback((name, avatarId, isKids = false) => {
    if (!user) return;
    const newProfile = {
      id:       `p${Date.now()}`,
      name:     name.trim(),
      avatarId: avatarId ?? Math.floor(Math.random() * AVATARS.length),
      isKids,
      isDefault: false,
    };
    const updated = [...profiles, newProfile];
    setProfiles(updated);
    saveProfiles(user.uid, updated);
    return newProfile;
  }, [user, profiles]);

  const updateProfile = useCallback((id, changes) => {
    if (!user) return;
    const updated = profiles.map(p => p.id === id ? { ...p, ...changes } : p);
    setProfiles(updated);
    saveProfiles(user.uid, updated);
    if (activeProfile?.id === id) setActiveProfile(prev => ({ ...prev, ...changes }));
  }, [user, profiles, activeProfile]);

  const deleteProfile = useCallback((id) => {
    if (!user) return;
    const updated = profiles.filter(p => p.id !== id);
    setProfiles(updated);
    saveProfiles(user.uid, updated);
    if (activeProfile?.id === id) {
      const fallback = updated[0];
      setActiveProfile(fallback);
      if (fallback) saveActiveId(user.uid, fallback.id);
    }
  }, [user, profiles, activeProfile]);

  return (
    <ProfileContext.Provider value={{
      profiles,
      activeProfile,
      showSelector,
      setShowSelector,
      selectProfile,
      addProfile,
      updateProfile,
      deleteProfile,
    }}>
      {children}
    </ProfileContext.Provider>
  );
}

export function useProfiles() { return useContext(ProfileContext); }
